import { useState } from 'react';
import { ErrorBanner, Loading, PageHead } from '../components/ui';
import { useResource } from '../lib/hooks';
import { MaDataRoom } from './ma/MaDataRoom';
import { MaGwc } from './ma/MaGwc';
import { MaIntegrationPlan } from './ma/MaIntegrationPlan';
import { MaDdRequests } from './ma/MaDdRequests';
import { MaToolbox } from './ma/MaToolbox';

/**
 * M&A — the acquisition hub.
 *
 * Ported from the standalone M&A Toolbox. This page is the index; each module
 * is its own page underneath. The counts are read from the same endpoint the
 * modules use, so the hub cannot claim a number the module then disagrees
 * with.
 */
export interface MaSummary {
  ToolboxTotal: number;
  ToolboxDone: number;
  DataRoomTotal: number;
  DataRoomReceived: number;
  DdTotal: number;
  DdComplete: number;
  DdInProgress: number;
  Plans: number;
  PlanSteps: number;
  GwcPeople: number;
  GwcRightSeat: number;
  CoreValues: number;
}

type Tab = 'overview' | 'toolbox' | 'dataroom' | 'dd' | 'plan' | 'gwc';

export function MaPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const summary = useResource<MaSummary>('/ma/summary');

  if (summary.loading) return <Loading label="Loading M&A" />;
  if (summary.error) return <ErrorBanner message={summary.error} />;
  const s = summary.data;

  return (
    <>
      <PageHead
        title="M&A"
        lede="Running an acquisition: the post-close integration playbook, the due diligence data room, the formal request list, and the people call on the staff coming across."
      />

      <div className="tabs">
        <button className={`tab${tab === 'overview' ? ' active' : ''}`} onClick={() => setTab('overview')}>
          Overview
        </button>
        <button className={`tab${tab === 'toolbox' ? ' active' : ''}`} onClick={() => setTab('toolbox')}>
          Acquisition Toolbox
        </button>
        <button className={`tab${tab === 'dataroom' ? ' active' : ''}`} onClick={() => setTab('dataroom')}>
          Data Room
        </button>
        <button className={`tab${tab === 'dd' ? ' active' : ''}`} onClick={() => setTab('dd')}>
          DD Request List
        </button>
        <button className={`tab${tab === 'plan' ? ' active' : ''}`} onClick={() => setTab('plan')}>
          Integration Plan
        </button>
        <button className={`tab${tab === 'gwc' ? ' active' : ''}`} onClick={() => setTab('gwc')}>
          GWC
        </button>
      </div>

      {tab === 'toolbox' && <MaToolbox />}
      {tab === 'dataroom' && <MaDataRoom />}
      {tab === 'dd' && <MaDdRequests />}
      {tab === 'plan' && <MaIntegrationPlan isAdmin />}
      {tab === 'gwc' && <MaGwc />}

      {tab === 'overview' && s && (
        <div className="grid grid-2">
          <ModuleCard
            onOpen={() => setTab('toolbox')}
            title="Acquisition Toolbox"
            blurb="The post-close integration playbook — 66 items across seven workstreams, each tagged with the phase it belongs to."
            done={s.ToolboxDone}
            total={s.ToolboxTotal}
            unit="items done"
          />
          <ModuleCard
            onOpen={() => setTab('dataroom')}
            title="Data Room"
            blurb="The A–I due diligence folder index. Structure only — no documents are held in this app."
            done={s.DataRoomReceived}
            total={s.DataRoomTotal}
            unit="received"
          />
          <ModuleCard
            onOpen={() => setTab('dd')}
            title="DD Request List"
            blurb="The formal buyer-side request list, with the data room reference and each party's response."
            done={s.DdComplete}
            total={s.DdTotal}
            unit="complete"
            note={s.DdInProgress > 0 ? `${s.DdInProgress} in progress` : undefined}
          />
          <ModuleCard
            onOpen={() => setTab('plan')}
            title="Integration Plan"
            blurb="The board of workstreams and steps for each acquisition, with a checklist inside every step."
            done={s.PlanSteps}
            total={s.PlanSteps}
            unit={`steps across ${s.Plans} ${s.Plans === 1 ? 'plan' : 'plans'}`}
          />
          <ModuleCard
            onOpen={() => setTab('gwc')}
            title="GWC"
            blurb="The EOS People Analyzer for the staff coming across. Get it, Want it, Capacity — each a straight yes or no."
            done={s.GwcRightSeat}
            total={s.GwcPeople}
            unit="right person, right seat"
            note={
              s.CoreValues === 0
                ? 'No core values yet — the right-person half needs them'
                : undefined
            }
          />
        </div>
      )}
    </>
  );
}

function ModuleCard({
  title,
  blurb,
  done,
  total,
  unit,
  note,
  onOpen,
  comingSoon,
}: {
  title: string;
  blurb: string;
  done: number;
  total: number;
  unit: string;
  note?: string;
  onOpen?: () => void;
  comingSoon?: boolean;
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  /* A card that goes somewhere is a button; one whose module is not built yet
     says so rather than looking clickable and doing nothing. */
  return (
    <div
      className={`card${onOpen ? ' ma-card-open' : ''}`}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => {
        if (onOpen && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="card-body">
        <div className="row-between">
          <h3>{title}</h3>
          {comingSoon && <span className="badge badge-neutral">Not built yet</span>}
          {onOpen && <span className="xs" style={{ color: 'var(--text-accent)' }}>Open →</span>}
        </div>
        <p className="small muted mt-2">{blurb}</p>

        {total === 0 ? (
          <p className="small subtle mt-4">Nothing here yet.</p>
        ) : (
          <>
            <div className="row mt-4">
              <span className="mono" style={{ fontSize: 'var(--text-2xl)', fontWeight: 600 }}>
                {done}
              </span>
              <span className="subtle">/ {total}</span>
              <span className="xs muted">{unit}</span>
            </div>
            {/* A bar rather than a number alone: "15 of 104" is a fraction you
                have to do in your head to feel. */}
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background: 'var(--surface-sunken)',
                overflow: 'hidden',
                marginTop: 'var(--space-2)',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'var(--border-strong)',
                }}
              />
            </div>
          </>
        )}

        {note && <p className="xs mt-2" style={{ color: 'var(--text-accent)' }}>{note}</p>}
      </div>
    </div>
  );
}
